import { CloudUpload, RotateCcw, TriangleAlert } from "lucide-react";
import type React from "react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { LogsContainer } from "~/components/job-log";
import { Button, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui";
import { useCompoundRecordComposersUpload } from "~/features/compoundRecordComposers/hooks";
import type {
  CompoundRecordComposerCandidateIdType,
  InsertedRecord,
} from "~/features/compoundRecordComposers/types-upload";
import {
  COMPOUND_RECORD_COMPOSER_CANDIDATE_ID_TYPES,
  DECISIONS,
} from "~/features/compoundRecordComposers/types-upload";
import type { CompoundRecordComposerCsvRow } from "~/features/compoundRecordComposers/utils";

const PREVIEW_LIMIT = 100;

export function CompoundRecordComposersUpload() {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    csvData,
    error,
    isLoading,
    isUploading,
    insertedRecords,
    jobRunId,
    maxRows,
    maxFileSizeBytes,
    resetKey,
    candidateIdType,
    setCandidateIdType,
    handleFileUpload,
    handleUpload,
    handleReset,
  } = useCompoundRecordComposersUpload();

  const exceedsLimit = csvData.length > maxRows;

  return (
    <div className="mx-auto max-w-[var(--content-max)] space-y-5 p-8">
      <div className="flex items-center gap-4">
        <Button type="button" variant="ghost" size="sm" asChild className="-ml-2">
          <Link to="..">{t("ui.backToList")}</Link>
        </Button>
      </div>

      <h1 className="text-dewey-title">{t("feature.compoundRecordComposers.upload.title")}</h1>

      <div className="rounded-[var(--radius-md)] border border-border-default bg-white p-6 space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-14-regular text-fg-2">
            {t("feature.compoundRecordComposers.upload.fileUploadLimit", {
              max: maxRows,
              maxFileSize: maxFileSizeBytes / 1_000_000,
            })}
          </p>
          {csvData.length > 0 && (
            <Button type="button" variant="outline" size="sm" onClick={handleReset}>
              <RotateCcw />
              <span>{t("feature.compoundRecordComposers.upload.resetFile")}</span>
            </Button>
          )}
        </div>

        <div className="flex flex-col gap-1.5 sm:max-w-[320px]">
          <label className="text-12-medium text-fg-2" htmlFor="compound-composer-candidate-id-type">
            {t("feature.compoundRecordComposers.upload.candidateIdType")}
          </label>
          <Select
            value={candidateIdType}
            onValueChange={(value) => setCandidateIdType(value as CompoundRecordComposerCandidateIdType)}
            disabled={isUploading}
          >
            <SelectTrigger id="compound-composer-candidate-id-type" className="h-9 text-14-regular">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={COMPOUND_RECORD_COMPOSER_CANDIDATE_ID_TYPES.RECORD_METADATA_ID}>
                {t("feature.compoundRecordComposers.upload.recordMetadataId")}
              </SelectItem>
              <SelectItem value={COMPOUND_RECORD_COMPOSER_CANDIDATE_ID_TYPES.RECORD_ID}>
                {t("feature.compoundRecordComposers.upload.recordId")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <label
          key={resetKey}
          className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-[var(--radius-md)] border border-dashed border-border-default bg-bg-subtle px-6 py-10 text-center transition-colors hover:border-primary-400 hover:bg-primary-50"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (isUploading) return;
            const file = e.dataTransfer.files[0];
            if (file)
              handleFileUpload({
                target: { files: e.dataTransfer.files },
              } as React.ChangeEvent<HTMLInputElement>);
          }}
        >
          <span className="text-14-regular text-fg-2">{t("ui.dragAndDrop")}</span>
          <span className="text-12-regular text-fg-3">{t("ui.acceptedFileTypes", { accept: ".csv" })}</span>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileUpload}
            disabled={isUploading}
            className="sr-only"
          />
        </label>

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-danger/30 bg-danger/5 px-3.5 py-2.5 text-14-regular text-danger">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {isLoading && (
          <p className="text-14-regular text-fg-3">{t("feature.compoundRecordComposers.upload.loadingFile")}</p>
        )}
      </div>

      {csvData.length > 0 && (
        <UploadPreview
          csvData={csvData}
          exceedsLimit={exceedsLimit}
          maxRows={maxRows}
          isUploading={isUploading}
          isLoading={isLoading}
          onUpload={handleUpload}
        />
      )}

      {insertedRecords.length > 0 && <InsertedRecordsPanel insertedRecords={insertedRecords} onReset={handleReset} />}

      {jobRunId && (
        <div className="space-y-4 rounded-[var(--radius-md)] border border-border-default bg-white p-6">
          <div>
            <h2 className="text-16-bold">{t("ui.jobRunLogs")}</h2>
            <p className="break-all font-mono text-12-regular text-fg-3">
              {t("model.common.id")}: {jobRunId}
            </p>
          </div>
          <LogsContainer jobRun={{ jobRunId, jobName: "CompoundRecordJob" }} />
        </div>
      )}
    </div>
  );
}

interface UploadPreviewProps {
  csvData: CompoundRecordComposerCsvRow[];
  exceedsLimit: boolean;
  maxRows: number;
  isUploading: boolean;
  isLoading: boolean;
  onUpload: () => void;
}

function UploadPreview({ csvData, exceedsLimit, maxRows, isUploading, isLoading, onUpload }: UploadPreviewProps) {
  const { t } = useTranslation();

  const similar = csvData.filter((r) => r.decision === DECISIONS.SIMILAR).length;
  const notSimilar = csvData.filter((r) => r.decision === DECISIONS.NOT_SIMILAR).length;
  const inconclusive = csvData.filter((r) => r.decision === DECISIONS.INCONCLUSIVE).length;

  return (
    <div className="space-y-4 rounded-[var(--radius-md)] border border-border-default bg-white p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-16-bold">
          {t("feature.compoundRecordComposers.upload.uploadPreview", {
            count: csvData.length,
          })}
        </h2>
        <Button type="button" onClick={onUpload} disabled={isUploading || isLoading || exceedsLimit} size="sm">
          <CloudUpload />
          <span>
            {isUploading
              ? t("feature.compoundRecordComposers.upload.uploading")
              : t("feature.compoundRecordComposers.upload.uploadButton")}
          </span>
        </Button>
      </div>

      {exceedsLimit && (
        <div className="flex items-start gap-2 rounded-[var(--radius-md)] border border-danger/30 bg-danger/5 px-3.5 py-2.5 text-14-regular text-danger">
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {t("feature.compoundRecordComposers.upload.exceedsLimit", {
              count: csvData.length,
              max: maxRows,
            })}
          </span>
        </div>
      )}

      <div className="rounded-[var(--radius-md)] border border-border-default bg-bg-subtle px-3.5 py-2 text-14-regular text-fg-2">
        {t("feature.compoundRecordComposers.upload.summaryStats", {
          similar,
          notSimilar,
          inconclusive,
        })}
      </div>

      <div className="max-h-[600px] overflow-auto rounded-[var(--radius-md)] border border-border-default">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 bg-bg-subtle">
            <tr>
              <Th>{t("feature.compoundRecordComposers.upload.mainCandidateId")}</Th>
              <Th>{t("feature.compoundRecordComposers.upload.compoundCandidateId")}</Th>
              <Th>{t("feature.compoundRecordComposers.upload.decision")}</Th>
              <Th>{t("feature.compoundRecordComposers.upload.memo")}</Th>
            </tr>
          </thead>
          <tbody>
            {csvData.slice(0, PREVIEW_LIMIT).map((row, i) => (
              <tr key={i} className="border-t border-border-default">
                <Td className="font-mono text-12-regular">{row.mainCandidateId}</Td>
                <Td className="font-mono text-12-regular">{row.compoundCandidateId}</Td>
                <Td>
                  <DecisionBadge decision={row.decision} />
                </Td>
                <Td className="max-w-[200px] truncate text-fg-2">{row.memo || "—"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {csvData.length > PREVIEW_LIMIT && (
        <p className="text-12-regular text-fg-3">
          {t("feature.compoundRecordComposers.upload.showingRows", {
            count: csvData.length,
          })}
        </p>
      )}
    </div>
  );
}

interface InsertedRecordsPanelProps {
  insertedRecords: InsertedRecord[];
  onReset: () => void;
}

function InsertedRecordsPanel({ insertedRecords, onReset }: InsertedRecordsPanelProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4 rounded-[var(--radius-md)] border border-border-default bg-white p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-16-bold">
          {t("feature.compoundRecordComposers.upload.insertedRecords", {
            count: insertedRecords.length,
          })}
        </h2>
        <Button type="button" variant="outline" size="sm" onClick={onReset}>
          <RotateCcw />
          <span>{t("feature.compoundRecordComposers.upload.clearResults")}</span>
        </Button>
      </div>

      <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-bg-subtle">
              <Th>{t("feature.compoundRecordComposers.upload.mainCandidateId")}</Th>
              <Th>{t("feature.compoundRecordComposers.upload.compoundCandidateId")}</Th>
              <Th>{t("feature.compoundRecordComposers.upload.decision")}</Th>
              <Th>{t("feature.compoundRecordComposers.upload.memo")}</Th>
              <Th>{t("feature.compoundRecordComposers.upload.error")}</Th>
            </tr>
          </thead>
          <tbody>
            {insertedRecords.map((record, i) => (
              <tr key={i} className="border-t border-border-default">
                <Td className="font-mono text-12-regular">{record.properties?.mainCandidateId || "—"}</Td>
                <Td className="font-mono text-12-regular">{record.properties?.compoundCandidateId || "—"}</Td>
                <Td>
                  <DecisionBadge decision={record.properties?.decision || ""} />
                </Td>
                <Td className="max-w-[200px] truncate text-fg-2">{record.properties?.memo || "—"}</Td>
                <Td className={record.error ? "text-danger" : "text-fg-3"}>{record.error || "—"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DecisionBadge({ decision }: { decision: string }) {
  const color =
    decision === DECISIONS.SIMILAR
      ? "bg-success/10 text-success"
      : decision === DECISIONS.NOT_SIMILAR
        ? "bg-danger/10 text-danger"
        : "bg-bg-subtle text-fg-2";

  return (
    <span className={`inline-flex rounded-[var(--radius-pill)] px-2 py-0.5 text-12-medium ${color}`}>
      {decision || "—"}
    </span>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={`border-b border-border-default px-3.5 py-2.5 text-left text-10-regular uppercase tracking-[0.06em] text-fg-2 ${className ?? ""}`}
    >
      {children}
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-3.5 py-2 text-14-regular align-middle text-fg-1 ${className ?? ""}`}>{children}</td>;
}
